import type React from "react"
import { useState, useEffect, useRef } from "react"
import { useBlocker, useNavigate, useParams } from "react-router-dom"
import api, { getSiteById } from "@/services/api"
import "@/styles/SiteRegistrationPage.css"
import { RiArrowLeftLine } from "react-icons/ri"
import useAutosizeTextArea from "@/hooks/useAutosizeTextArea"
import { PlaceSearchModal } from "@/components/community/PlaceSearchModal"
import type { PlaceData } from "@/components/community/PlaceSelector"

type ApiTag = {
  id: number
  name: string
  tagGroup: string
  tagGroupTitle: string
  icon?: string
}

type TagGroup = {
  id: string
  tagGroupTitle: string
  tags: { id: number; name: string; icon?: string }[]
}

const SiteRegistrationPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [isEditMode, setIsEditMode] = useState(!!id)
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phoneNumber: "",
    description: "",
    latitude: "",
    longitude: "",
    tagIds: [] as number[],
  })
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [allImageIds, setAllImageIds] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isInitialLoad = useRef(true)
  const [tags, setTags] = useState<TagGroup[]>([])
  const [tagLoading, setTagLoading] = useState(true)
  const [tagError, setTagError] = useState<string | null>(null)
  const [imageUploadLoading, setImageUploadLoading] = useState(false)
  const [imageUploadError, setImageUploadError] = useState<string | null>(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  
  // Map Modal State
  const [isMapModalOpen, setIsMapModalOpen] = useState(false)
  const [initialMapLocation, setInitialMapLocation] = useState<{lat: number, lng: number} | undefined>(undefined)

  useAutosizeTextArea(textAreaRef.current, formData.description)

  // Fetch existing site data in edit mode
  useEffect(() => {
    if (id) {
      setIsEditMode(true)
      const fetchSiteData = async () => {
        try {
          setLoading(true)
          const siteData = await getSiteById(id)
          setFormData({
            name: siteData.name,
            address: siteData.address,
            phoneNumber: siteData.phoneNumber || "",
            description: siteData.description || "",
            latitude: siteData.latitude.toString(),
            longitude: siteData.longitude.toString(),
            tagIds: siteData.tags.map(t => t.id),
          })
          setImagePreviews(siteData.imageUrls)
          setAllImageIds(siteData.imageIds || [])
          // In edit mode, we don't have the original files, just URLs.
          // The user will have to re-upload images if they want to change them.
          // For simplicity, we can clear existing images if new ones are added.
        } catch (err) {
          setError("캠핑장 정보를 불러오는데 실패했습니다.")
        } finally {
          setLoading(false)
        }
      }
      void fetchSiteData()
    }
  }, [id])

  // 폼이 수정되었는지 감지
  useEffect(() => {
    // 초기 로딩 중에는 더티 체크를 수행하지 않음
    if (isInitialLoad.current) {
        if (!loading && !tagLoading) {
            isInitialLoad.current = false
        }
        return
    }
    
    setIsDirty(true)
  }, [formData, imageFiles, loading, tagLoading])

  // 태그 목록 불러오기
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await api.get<Record<string, ApiTag[]>>("/api/tags")
        if (typeof response.data === "object" && response.data !== null) {
          const transformedTags: TagGroup[] = []
          for (const groupName in response.data) {
            if (
              Object.prototype.hasOwnProperty.call(response.data, groupName)
            ) {
              const apiTagsInGroup = response.data[groupName]
              if (apiTagsInGroup.length > 0) {
                const tagGroupTitle = apiTagsInGroup[0].tagGroupTitle
                const tagsForGroup = apiTagsInGroup.map(apiTag => ({
                  id: apiTag.id,
                  name: apiTag.name,
                  icon: apiTag.icon,
                }))
                transformedTags.push({
                  id: groupName, // Use group name as a key
                  tagGroupTitle,
                  tags: tagsForGroup,
                })
              }
            }
          }
          setTags(transformedTags)
        } else {
          setTagError("태그 데이터 형식이 올바르지 않습니다.")
        }
      } catch (err) {
        setTagError("태그를 불러오는데 실패했습니다.")
      } finally {
        setTagLoading(false)
      }
    }
    void fetchTags()
  }, [])

  const blocker = useBlocker(isDirty && !isSubmitting)

  useEffect(() => {
    if (blocker?.state === "blocked") {
      if (
        window.confirm(
          "페이지를 벗어나시겠습니까? 변경사항이 저장되지 않을 수 있습니다.",
        )
      ) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files)

      setImageUploadLoading(true)
      setImageUploadError(null)
      try {
        const uploadPromises = files.map(file => {
          const formData = new FormData()
          formData.append("file", file)
          return api.post<{
            imageId: number
            latitude: number
            longitude: number
          }>("/api/camping-sites/upload-image-for-metadata", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
        })
        const responses = await Promise.all(uploadPromises)
        const newImageIds = responses.map(res => res.data.imageId)
        
        // Check for GPS data in uploaded images
        let foundLocation = false
        for (const res of responses) {
            if (res.data.latitude !== 0 && res.data.longitude !== 0) {
                setInitialMapLocation({
                    lat: res.data.latitude,
                    lng: res.data.longitude
                })
                foundLocation = true
                
                // Optional: Automatically fill address/location if fields are empty?
                // For now, just setting initialMapLocation so when user clicks "Address Search", it opens there.
                // Or we could auto-open the map modal? Let's be less intrusive and just act as "ready".
                break // Use first found location
            }
        }
        
        if (foundLocation) {
             // Maybe show a toast or message saying "Location found in image. Click address search to use it."
             console.log("GPS found in image, updated initial map location.")
        }

        setImageFiles(prev => [...prev, ...files])
        setImagePreviews(prev => [
          ...prev,
          ...files.map(f => URL.createObjectURL(f)),
        ])
        setAllImageIds(prev => [...prev, ...newImageIds])
      } catch (err) {
        setImageUploadError("이미지 업로드에 실패했습니다.")
      } finally {
        setImageUploadLoading(false)
      }
    }
  }

  const handleTagClick = (tagId: number) => {
    setFormData(prev => {
      const newTagIds = prev.tagIds.includes(tagId)
        ? prev.tagIds.filter(id => id !== tagId)
        : [...prev.tagIds, tagId]
      return { ...prev, tagIds: newTagIds }
    })
  }

  const handleRemoveImage = (index: number) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index))
    setAllImageIds(prev => prev.filter((_, i) => i !== index))
    
    // 새로 업로드한 파일도 제거 (imageFiles는 새로 업로드한 것만 포함)
    const existingCount = allImageIds.length - imageFiles.length
    if (index >= existingCount) {
      const fileIndex = index - existingCount
      setImageFiles(prev => prev.filter((_, i) => i !== fileIndex))
    }
  }

  const handleOpenAddressSearch = () => {
    // Open our new PlaceSearchModal instead of window.open
    setIsMapModalOpen(true)
  }
  
  const handlePlaceSelect = (place: PlaceData) => {
      setFormData(prev => ({
          ...prev,
          address: place.address,
          latitude: place.lat.toString(),
          longitude: place.lng.toString()
      }))
      setIsMapModalOpen(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    if (allImageIds.length === 0) {
      setError("하나 이상의 이미지를 등록해야 합니다.")
      setLoading(false)
      return
    }

    if (!formData.address) {
      setError("주소를 검색하여 등록해주세요.")
      setLoading(false)
      return
    }

    try {
      const requestData = {
        ...formData,
        latitude: parseFloat(formData.latitude) || 0,
        longitude: parseFloat(formData.longitude) || 0,
        imageIds: allImageIds,
      }

      if (isEditMode && id) {
        await api.put(`/api/camping-sites/${id}`, requestData)
        // alert 사용 대신 부드러운 전환을 위해 navigate 바로 수행
        setIsSubmitting(true)
        setIsDirty(false)
        navigate(`/sites/${id}`, { replace: true })
      } else {
        await api.post("/api/camping-sites", requestData)
        // UX: alert 대신 관리자 페이지나 목록으로 이동 (필요 시 토스트 등 도입 가능)
        setIsSubmitting(true)
        setIsDirty(false)
        navigate("/map", { replace: true })
      }
      setIsDirty(false)
    } catch (err: any) {
      setError(err.response?.data?.message || "처리 중 오류가 발생했습니다.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="registration-page-container">
      <button
        className="back-button"
        onClick={() => navigate(-1)}
      >
        <RiArrowLeftLine />
      </button>

      <form onSubmit={handleSubmit} className="camping-site-form">
        <h2>{isEditMode ? "캠핑장 정보 수정" : "캠핑장 등록 제안"}</h2>
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label htmlFor="images">이미지 (다중 선택 가능) *</label>
          <input
            type="file"
            id="images"
            accept="image/jpeg, image/jpg"
            onChange={handleImageChange}
            multiple
          />
          {imageUploadLoading && <p>이미지 업로드 중...</p>}
          {imageUploadError && (
            <div className="error-message">{imageUploadError}</div>
          )}
          <div className="image-preview-container">
            {imagePreviews.map((previewUrl, index) => (
              <div key={index} className="image-preview-item">
                <img src={previewUrl} alt={`미리보기 ${index + 1}`} />
                <button type="button" onClick={() => { handleRemoveImage(index); }}>
                  삭제
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="address">주소 *</label>
          <div className="address-search-container">
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              readOnly
              placeholder="주소 검색 버튼을 눌러 주소를 찾아주세요."
            />
            <button type="button" onClick={handleOpenAddressSearch}>
              위치/주소 검색
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber">전화번호</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            placeholder="010-1234-5678"
          />
        </div>

        <div className="form-group">
          <label htmlFor="name">캠핑장 이름</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">설명</label>
          <textarea
            id="description"
            name="description"
            ref={textAreaRef}
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            placeholder="캠핑장에 대한 설명을 입력해주세요."
          />
        </div>

        <div className="form-group">
          <label>태그 선택</label>
          {tagLoading && <p>태그 불러오는 중...</p>}
          {tagError && <div className="error-message">{tagError}</div>}
          {!tagLoading &&
            !tagError &&
            tags.map(tagGroup => (
              <div key={tagGroup.id} className="tag-group">
                <h3>{tagGroup.tagGroupTitle}</h3>
                <div className="tags-container">
                  {tagGroup.tags.map(tag => {
                    const isSelected = formData.tagIds.includes(tag.id)
                    return (
                      <div
                        key={tag.id}
                        className={`tag-item ${isSelected ? "selected" : ""}`}
                        onClick={() => { handleTagClick(tag.id); }}
                      >
                        {tag.icon && /\p{Emoji}/u.test(tag.icon) && (
                          <span className="tag-icon">{tag.icon}</span>
                        )}
                        <span className="tag-name">{tag.name}</span>
                        {isSelected && (
                          <button
                            className="tag-remove-button"
                            onClick={e => {
                              e.stopPropagation()
                              handleTagClick(tag.id)
                            }}
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
        </div>

        <button type="submit" disabled={loading}>
          {loading
            ? isEditMode
              ? "수정 중..."
              : "등록 요청 중..."
            : isEditMode
            ? "수정하기"
            : "등록 제안하기"}
        </button>
      </form>

      <PlaceSearchModal
        isOpen={isMapModalOpen}
        onClose={() => { setIsMapModalOpen(false); }}
        onConfirm={handlePlaceSelect}
        mode="SELECT_COORDINATE"
        initialLocation={initialMapLocation}
      />
    </div>
  )
}

export default SiteRegistrationPage

